import { useEffect, useState } from "react";
import type { EntityType, EventOption, InteractionEvent } from "../../types";
import {
  MIN_HISTORY_VISITS,
  START_STEP,
  STRUCTURE_EDIT,
  countStepTransitions,
  setStepChoices,
  stepChoices,
  suggestStepOrder,
  timesSeen,
} from "../../utils/stepOrder";
import { StepFlowEditor } from "./StepFlowEditor";
import { LogActivitySheet } from "../interactions/LogActivitySheet";
import "./StepOrderSheet.css";

/** Past activities to learn an order from, e.g. "Hagrid" or "all Attractions". */
export interface StepHistory {
  label: string;
  activities: InteractionEvent[][];
}

/** Where the order can be saved, e.g. this place or the whole type. */
export interface StepOrderTarget {
  key: string;
  label: string;
  /** Why it can't be used right now, if it can't. */
  disabledReason?: string | null;
}

interface StepOrderSheetProps {
  open: boolean;
  onClose: () => void;
  /** The steps as they run, flow and all. */
  options: EventOption[];
  /** Which point to open at: a step's id, or START_STEP. */
  initialStepId: string;
  title?: string;
  /** Ways to save, given what's been changed; the first is the main button. */
  targets: (edited: Set<string>, draft: EventOption[]) => StepOrderTarget[];
  onSave: (options: EventOption[], editedSteps: Set<string>, target: string) => Promise<void> | void;
  /** Loads past activities, closest first (this place, then its type). */
  loadHistory?: () => Promise<StepHistory[]>;
  /** A line under the title, e.g. what saving for the type will and won't reach. */
  note?: string | null;
  /** The place's type, which decides the special step kinds offered. */
  entityType?: EntityType | string | null;
}

/**
 * A place's steps in a sheet, opened from "Edit buttons" at the step you're
 * on: the same step list as the place and type pages (see StepFlowEditor),
 * plus "Suggest from my history" and saving for this place or its type.
 */
export function StepOrderSheet({
  open,
  onClose,
  options: initialOptions,
  initialStepId,
  title,
  targets: targetsFor,
  onSave,
  loadHistory,
  note,
  entityType,
}: StepOrderSheetProps) {
  const [draft, setDraft] = useState(initialOptions);
  // Bumped on each opening, so the list starts with the right step open.
  const [editorKey, setEditorKey] = useState(0);
  const [edited, setEdited] = useState<Set<string>>(() => new Set());
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [suggestNote, setSuggestNote] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  // Start over each time it opens, from the steps as they are now.
  useEffect(() => {
    if (!open) return;
    setDraft(initialOptions);
    setEditorKey((k) => k + 1);
    setEdited(new Set());
    setError(null);
    setSuggestNote(null);
    // Only on opening; the options passed in can re-render underneath.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The place's steps can arrive after the sheet opens (tapped while the card
  // was still loading them). Until something's been changed, follow them.
  useEffect(() => {
    if (!open || edited.size > 0) return;
    setDraft(initialOptions);
    setEditorKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOptions]);

  // Every point the history can speak to: the start, then each step.
  const points = [START_STEP, ...draft.map((o) => o.id)];

  const suggest = async () => {
    if (!loadHistory) return;
    setSuggesting(true);
    setSuggestNote(null);
    try {
      const sources = await loadHistory();
      const counted = sources.map((s) => ({ ...s, counts: countStepTransitions(draft, s.activities) }));
      const usable = counted.filter((s) => s.activities.length > 0);
      const source = usable.find((s) => s.activities.length >= MIN_HISTORY_VISITS) ?? usable[usable.length - 1];
      if (!source) {
        setSuggestNote("Nothing logged here yet to learn from.");
        return;
      }
      let next = draft;
      const changed = new Set<string>();
      for (const p of points) {
        if (timesSeen(source.counts, p) === 0) continue;
        const ordered = suggestStepOrder(next, source.counts, p);
        if (ordered.join() !== stepChoices(next, p).join()) {
          next = setStepChoices(next, p, ordered);
          changed.add(p);
        }
      }
      setDraft(next);
      setEdited((e) => new Set([...e, ...changed]));
      const visits = `${source.activities.length} ${source.activities.length === 1 ? "visit" : "visits"}`;
      setSuggestNote(
        changed.size === 0
          ? `Already matches your ${visits} (${source.label}).`
          : `Reordered ${changed.size} ${changed.size === 1 ? "step" : "steps"} from your ${visits} (${source.label}). Check them, then save.`,
      );
    } catch {
      setSuggestNote("Couldn't load your history.");
    } finally {
      setSuggesting(false);
    }
  };

  const save = async (target: string) => {
    setSaving(target);
    setError(null);
    try {
      await onSave(draft, edited, target);
      onClose();
    } catch {
      setError("Couldn't save. Check your connection and try again.");
    } finally {
      setSaving(null);
    }
  };

  const targets = targetsFor(edited, draft);

  return (
    <LogActivitySheet open={open} onClose={onClose} title={title ?? "Steps"}>
      <div className="so">
        {note && <p className="so-note">{note}</p>}

        <StepFlowEditor
          key={editorKey}
          options={draft}
          initialOpenId={initialStepId}
          entityType={entityType}
          onChange={(next, edit) => {
            setDraft(next);
            setEdited((e) => new Set(e).add(edit.stepId ?? STRUCTURE_EDIT));
          }}
        />

        {loadHistory && (
          <div className="so-suggest">
            <button type="button" className="btn btn-secondary btn-small" onClick={suggest} disabled={suggesting}>
              {suggesting ? "Looking at your history…" : "Suggest from my history"}
            </button>
            {suggestNote && <span className="so-hint" role="status">{suggestNote}</span>}
          </div>
        )}

        {error && <p className="so-error" role="alert">{error}</p>}

        <div className="so-save">
          {targets.map((t, i) => (
            <button
              key={t.key}
              type="button"
              className={i === 0 ? "btn" : "btn btn-secondary"}
              disabled={edited.size === 0 || !!saving || !!t.disabledReason}
              title={t.disabledReason ?? undefined}
              onClick={() => save(t.key)}
            >
              {saving === t.key ? "Saving…" : t.label}
            </button>
          ))}
        </div>
        {targets.some((t) => t.disabledReason) && (
          <p className="so-hint">{targets.find((t) => t.disabledReason)!.disabledReason}</p>
        )}
      </div>
    </LogActivitySheet>
  );
}
